/*----------------------------------
- CATALOG
----------------------------------*/

import { BarChart3, Bug, Database, Gauge, History, ListTodo, Settings, Users, Workflow, type Icon } from 'lucide-preact';

import type { AutomationDashboardSectionKey } from '@/common/types/automationControlTower';

/*----------------------------------
- TYPES
----------------------------------*/

export type OpsShellArea = 'automation' | 'cpDev';
export type CpDevOpsShellKey = 'job-searches' | 'logs' | 'issues';

export interface OpsShellNavigationEntry<TKey extends string = string> {
    key: TKey;
    label: string;
    href: string;
    testId: string;
    icon: Icon;
    matches: (pathname: string) => boolean;
}

export interface OpsShellAreaConfig {
    subtitle: string;
    navigationLabel: string;
    entries: Array<OpsShellNavigationEntry>;
}

export interface OpsShellNavigationGroup {
    key: OpsShellArea;
    label: string;
    testId: string;
    icon: Icon;
    entries: Array<OpsShellNavigationEntry>;
}

/*----------------------------------
- HELPERS
----------------------------------*/

const isExactPath = (target: string) => (pathname: string): boolean => pathname === target;
const isExactOrNestedPath = (target: string) => (pathname: string): boolean =>
    pathname === target || pathname.startsWith(`${target}/`);

/*----------------------------------
- AUTOMATION
----------------------------------*/

export const AUTOMATION_OPS_SHELL_ENTRIES: Array<OpsShellNavigationEntry<AutomationDashboardSectionKey>> = [
    {
        key: 'overview',
        label: 'Overview',
        href: '/admin/automation',
        testId: 'automation-nav-overview',
        icon: Gauge,
        matches: isExactPath('/admin/automation'),
    },
    {
        key: 'users',
        label: 'Users',
        href: '/admin/automation/users',
        testId: 'automation-nav-users',
        icon: Users,
        matches: isExactOrNestedPath('/admin/automation/users'),
    },
    {
        key: 'accounts',
        label: 'Accounts',
        href: '/admin/automation/accounts',
        testId: 'automation-nav-accounts',
        icon: Database,
        matches: isExactOrNestedPath('/admin/automation/accounts'),
    },
    {
        key: 'tasks',
        label: 'Tasks',
        href: '/admin/automation/tasks',
        testId: 'automation-nav-tasks',
        icon: ListTodo,
        matches: isExactOrNestedPath('/admin/automation/tasks'),
    },
    {
        key: 'events',
        label: 'Events',
        href: '/admin/automation/events',
        testId: 'automation-nav-events',
        icon: History,
        matches: isExactOrNestedPath('/admin/automation/events'),
    },
    {
        key: 'sequences',
        label: 'Sequences',
        href: '/admin/automation/sequences',
        testId: 'automation-nav-sequences',
        icon: Workflow,
        matches: isExactOrNestedPath('/admin/automation/sequences'),
    },
    {
        key: 'usage',
        label: 'Budgets & Usage',
        href: '/admin/automation/usage',
        testId: 'automation-nav-usage',
        icon: BarChart3,
        matches: isExactOrNestedPath('/admin/automation/usage'),
    },
];

/*----------------------------------
- CP DEV
----------------------------------*/

export const CP_DEV_OPS_SHELL_ENTRIES: Array<OpsShellNavigationEntry<CpDevOpsShellKey>> = [
    {
        key: 'job-searches',
        label: 'Job Searches',
        href: '/dashboard/job-searches',
        testId: 'cp-dev-nav-job-searches',
        icon: Workflow,
        matches: isExactOrNestedPath('/dashboard/job-searches'),
    },
    {
        key: 'logs',
        label: 'Logs',
        href: '/admin/logs',
        testId: 'cp-dev-nav-logs',
        icon: Settings,
        matches: (pathname: string): boolean =>
            pathname === '/admin/logs' || pathname === '/admin/logs/warns' || pathname === '/admin/logs/activities',
    },
    {
        key: 'issues',
        label: 'Issues',
        href: '/admin/issues',
        testId: 'cp-dev-nav-issues',
        icon: Bug,
        matches: isExactOrNestedPath('/admin/issues'),
    },
];

/*----------------------------------
- EXPORTS
----------------------------------*/

export const OPS_SHELL_NAVIGATION: Record<OpsShellArea, OpsShellAreaConfig> = {
    automation: {
        subtitle: 'Automation',
        navigationLabel: 'Automation V2',
        entries: AUTOMATION_OPS_SHELL_ENTRIES,
    },
    cpDev: {
        subtitle: 'CP Dev',
        navigationLabel: 'CP Dev',
        entries: CP_DEV_OPS_SHELL_ENTRIES,
    },
};

export const OPS_SHELL_GROUPS: Array<OpsShellNavigationGroup> = [
    {
        key: 'automation',
        label: 'Automation',
        testId: 'ops-shell-group-automation',
        icon: Workflow,
        entries: AUTOMATION_OPS_SHELL_ENTRIES,
    },
    {
        key: 'cpDev',
        label: 'CP Dev',
        testId: 'ops-shell-group-cp-dev',
        icon: Bug,
        entries: CP_DEV_OPS_SHELL_ENTRIES,
    },
];

export const resolveOpsShellArea = (pathname: string): OpsShellArea =>
    pathname.startsWith('/admin/automation') ? 'automation' : 'cpDev';
